if (!customElements.get('avl-review-slider')) {
  customElements.define('avl-review-slider', class extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.avl-review-slider__track');
      if (!this.track) return;
      this.cards = [...this.track.children];
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.previous = this.querySelector('[data-review-prev]');
      this.next = this.querySelector('[data-review-next]');
      this.toggle = this.querySelector('[data-review-toggle]');
      this.viewport = this.querySelector('.avl-review-slider__window');
      this.controls = this.querySelector('.avl-review-slider__controls');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = false;
      this.hovered = false;
      this.visible = false;
      this.previous.addEventListener('click', () => this.move(-1), options);
      this.next.addEventListener('click', () => this.move(1), options);
      this.toggle.addEventListener('click', () => {
        this.userPaused = !this.userPaused;
        this.sync();
      }, options);
      this.track.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        this.hovered = true;
        this.sync();
      }, options);
      this.track.addEventListener('pointerleave', () => {
        this.hovered = false;
        this.sync();
      }, options);
      this.track.addEventListener('pointerdown', () => this.pause(), options);
      this.track.addEventListener('wheel', () => this.pause(), { ...options, passive: true });
      this.addEventListener('focusin', (event) => {
        if (event.target !== this.toggle) this.pause();
      }, options);
      this.track.addEventListener('scroll', () => this.update(), { ...options, passive: true });
      this.track.addEventListener('keydown', (event) => {
        if (event.target !== this.track) return;
        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
          event.preventDefault();
          this.move(event.key === 'ArrowLeft' ? -1 : 1);
        } else if (event.key === 'Home' || event.key === 'End') {
          event.preventDefault();
          this.pause();
          const last = this.cards[this.cards.length - 1];
          this.scrollTo(event.key === 'Home' ? 0 : last.offsetLeft - this.cards[0].offsetLeft);
        }
      }, options);
      document.addEventListener('visibilitychange', () => this.sync(), options);
      this.motion.addEventListener('change', () => this.layout(), options);
      this.intersection = new IntersectionObserver(([entry]) => {
        this.visible = entry.isIntersecting;
        this.sync();
      });
      this.intersection.observe(this.track);
      this.resize = new ResizeObserver(() => this.layout());
      this.resize.observe(this.track);
      this.layout();
    }
    disconnectedCallback() {
      this.stop();
      this.abort?.abort();
      this.resize?.disconnect();
      this.intersection?.disconnect();
      this.track?.querySelectorAll('[data-review-clone]').forEach((card) => card.remove());
      this.removeAttribute('data-loop');
    }
    layout() {
      this.stop();
      const fraction = this.cycle ? (this.track.scrollLeft % this.cycle) / this.cycle : 0;
      this.track.querySelectorAll('[data-review-clone]').forEach((card) => card.remove());
      this.cycle = 0;
      if (!this.motion.matches && this.cards.length > 1 && this.track.clientWidth) {
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const last = this.cards[this.cards.length - 1];
        const distance = last.offsetLeft - this.cards[0].offsetLeft + last.getBoundingClientRect().width + gap;
        const copies = Math.ceil(this.track.clientWidth / distance);
        for (let copy = 0; copy < copies; copy++) {
          this.cards.forEach((card) => {
            const clone = card.cloneNode(true);
            clone.setAttribute('data-review-clone', '');
            clone.setAttribute('aria-hidden', 'true');
            clone.querySelectorAll('a, button, [tabindex]').forEach((el) => el.setAttribute('tabindex', '-1'));
            this.track.append(clone);
          });
        }
        // Measure the exact repeat point, including the gap; no percentage-based jump.
        this.cycle = this.track.children[this.cards.length].offsetLeft - this.cards[0].offsetLeft;
      }
      this.toggleAttribute('data-loop', this.cycle > 0);
      this.track.scrollLeft = this.cycle ? fraction * this.cycle : this.track.scrollLeft;
      this.update();
      this.sync();
    }
    stop() {
      cancelAnimationFrame(this.frame);
      this.frame = null;
      this.lastTime = null;
    }
    pause() {
      this.userPaused = true;
      this.sync();
    }
    sync() {
      this.stop();
      this.toggle.hidden = !this.cycle;
      const t = (key, fallback) => (window.avlT_ ? window.avlT_(key) : fallback);
      this.toggle.setAttribute('aria-label', this.userPaused ? t('reviews.play', 'Play') : t('reviews.pause', 'Pause'));
      this.toggle.querySelector('[data-review-play]').hidden = !this.userPaused;
      this.toggle.querySelector('[data-review-pause]').hidden = this.userPaused;
      if (!this.cycle || this.motion.matches || this.userPaused || this.hovered || !this.visible || document.hidden) return;
      this.position = this.track.scrollLeft % this.cycle;
      const tick = (time) => {
        if (this.lastTime !== null) {
          // 44 px/s matches the reference's gentle continuous pace on both sizes.
          this.position = (this.position + Math.min(time - this.lastTime, 64) * .044) % this.cycle;
          this.track.scrollLeft = this.position;
        }
        this.lastTime = time;
        this.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    }
    scrollTo(left) {
      this.track.scrollTo({ left, behavior: this.motion.matches ? 'instant' : 'smooth' });
    }
    move(direction) {
      this.pause();
      if (this.cycle) {
        this.track.scrollLeft %= this.cycle;
        if (direction < 0 && this.track.scrollLeft <= 2) this.track.scrollLeft = this.cycle;
      }
      const cards = [...this.track.children];
      const origin = cards[0].offsetLeft;
      const positions = cards.map((card) => card.offsetLeft - origin);
      const current = this.track.scrollLeft;
      const target = direction > 0
        ? positions.find((position) => position > current + 2)
        : positions.reverse().find((position) => position < current - 2);
      this.scrollTo(target ?? (direction > 0 ? this.track.scrollWidth : 0));
    }
    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const atStart = !this.cycle && this.track.scrollLeft <= 2;
      const atEnd = !this.cycle && this.track.scrollLeft >= max - 2;
      this.previous.disabled = atStart;
      this.next.disabled = atEnd;
      this.viewport.toggleAttribute('data-at-start', atStart);
      this.viewport.toggleAttribute('data-at-end', atEnd);
      this.controls.hidden = max <= 2;
    }
  });
}

